import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import ActionDropdown from "../common/components/ActionDropdown";
import { DefaultButton } from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import Icon from "../common/components/Icon";
import useMessageDisplay from "../common/components/MessageDisplay";
import ReactForm from "../common/components/ReactForm";
import Select from "../common/components/Select";
import TextInput from "../common/components/TextInput";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from "../common/hooks/networkHook";
import { cdate } from "../common/utils/CDate";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import { createJobUpdate, useJobUpdates } from "../reducers/jobUpdateSlice";
import JobUpdate from "./JobUpdate";
import JobUpdateOperation from "./JobUpdateOperations";
import { useSpecializedTranslation } from '../hooks/specializedTranslationHook';
import { useJob } from '../reducers/jobSlice';

const JobUpdateButton = ({ jobid }) => {
    const job = useJob(jobid);
    const { t } = useSpecializedTranslation(job);
    const { showError } = useMessageDisplay()
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const jobUpdates = useJobUpdates(job.jobid);
    const [selection, setSelection] = useState();
    const [operation, setOperation] = useState();
    const [regenerate, setRegenerate] = useState(false);
    const [reason, setReason] = useState();
    const regenValidationRef = useRef();

    const handleSelect = useCallback(id => {
        if (id === 'regenerate') {
            setRegenerate(true);
        } else {
            setSelection({ config: clientConfig.jobUpdates?.configs?.find(c => c.id == id) });
        }
    }, [clientConfig]);

    const handleUpdate = useCallback(data => {
        setSelection(selection => ({ ...selection, update: data }));
    }, []);

    const operationCancel = useCallback(() => {
        if (operation !== 'preview') {
            setSelection();
            setReason();
            setRegenerate(false);
        }
        setOperation();
    }, [operation]);

    const options = useMemo(() => jobUpdates.map(u => ({ value: u.id, label: `${clientConfig.jobUpdates?.configs?.find(c => c.id == u.kindid)?.title} #${u.updatenumber}  - ${cdate(u.timestamp, clientConfig.tz).dateTimeWithSecondsFormat()}` })), [clientConfig.jobUpdates?.configs, clientConfig.tz, jobUpdates]);

    const handleRegenerate = useCallback(() => {
        if (regenValidationRef.current?.checkValidity()) {
            setRegenerate(false); 
            setOperation(selection?.config?.disposition ? 'email' : 'print')
        }
    }, [selection])

    const selectableUpdates = useMemo(() => {
        return clientConfig.jobUpdates?.configs?.filter(u => {
            return !u.applications || u.applications.some(set => 
                (!clientConfig.enableSites || set.site === null || set.site === job?.site)
                && (!clientConfig.enableLevels || set.level === null || set.level === job?.level)
                && (set.type === null || set.type === job?.type)
            )
        });
    }, [clientConfig.jobUpdates, clientConfig.enableSites, clientConfig.enableLevels, job?.site, job?.level, job?.type]);

    return <>
        <JobUpdateOperation config={selection?.config} update={selection?.update} operation={operation} reason={reason} onEmailSent={() => { setSelection(); setOperation(); setRegenerate(false); setReason(); }} onEmailError={() => showError(t('emailNotSent'))} onCancel={operationCancel}></JobUpdateOperation>
        <ReactNodeDialog title={t('regenerate')} showModal={regenerate && !operation} footerControls={<DefaultButton kind="preview" disabled={!selection?.update?.id} onClick={() => setOperation('preview')}></DefaultButton>} onSave={handleRegenerate} onClose={() => { setOperation(); setSelection(); setReason(); setRegenerate(false) }}>
            <ReactForm ref={regenValidationRef}>
                <Row>
                    <Col>
                        {t('jobUpdateRegenWarning')}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Select value={selection?.update?.id} onChange={id => { if (!id) { setSelection(); } else { const update = jobUpdates.find(u => u.id == id); setSelection({ update, config: clientConfig.jobUpdates.configs.find(c => c.id == update.kindid) }); }}} wildcard="" options={options} labelField="label" valueField="value" required></Select>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <TextInput value={reason} placeholder={t('reason')} live={true} onChange={setReason} required></TextInput>
                    </Col>
                </Row>
            </ReactForm>
        </ReactNodeDialog>
        <ReactNodeDialog title={selection?.config?.title ?? t('jobUpdate')} size="xlg" showModal={!!selection?.config && !operation && !regenerate} footerControls={<DefaultButton kind="preview" onClick={() => setOperation('preview')}></DefaultButton>} onSave={() => {
            if (!selection?.update) {
                throw new Error('No update data has been configured');
            }
            createJobUpdate(network, selection.config.allowEditing ? { 
                jobid,
                kindid: selection.update.kindid,
                notes: selection.update.jobnotes,
                images: selection.update.jobimages,
                job_fields: selection.update.job_fields,
                show_map: selection.update.show_map,
                reason: selection.update.reason
            } : {
                jobid,
                kindid: selection.update.kindid,
                show_map: selection.update.show_map,
                reason: selection.update.reason
            }).then(res => {
                setSelection(selection => ({ ...selection, update: res }));
                setOperation(selection?.config?.disposition ? 'email' : 'print');
            })
        }} invalid={!selection?.update?.reason} onClose={() => { setSelection(); }}>
            { !!selection?.config && !operation && !regenerate ? <><p>{t('jobUpdateCreateWarning')}</p><JobUpdate config={selection.config} update={selection.update} jobid={jobid} onChange={handleUpdate}></JobUpdate></>  : null }
        </ReactNodeDialog>
        { clientConfig.jobUpdates?.enable && (jobUpdates?.length > 0 || selectableUpdates?.length > 0) && <ActionDropdown onSelect={handleSelect} label={t('jobUpdate')} icon={<Icon variant="solid" kind="file-pen"></Icon>}>
            { selectableUpdates.map(c => <Dropdown.Item key={c.id} eventKey={c.id}>{c.title}</Dropdown.Item>) }
            { jobUpdates?.length > 0 && <Dropdown.Item eventKey="regenerate">{t('regenerate')}...</Dropdown.Item> }
        </ActionDropdown> }
    </>
}

JobUpdateButton.propTypes = {
    jobid: PropTypes.string
};

export default JobUpdateButton;
